import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Switch } from "@/shared/components/ui/switch";
import { ArrowLeft, Loader2 } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";

interface FeatureFlag {
  id: number;
  name: string;
  display_name: string;
  desc: string | null;
  enabled: boolean;
}

interface FlagCategory {
  label: string;
  flags: FeatureFlag[];
}

const FLAG_CATEGORIES: Record<string, string> = {
  document_split_enabled: "Documents",
  use_elayne_api: "Documents",
  notifications_enabled: "Notifications",
  elayne_email_bypass_enabled: "Notifications",
  email_verification_enabled: "Auth",
  google_oauth_enabled: "Auth",
  tracers_api_enabled: "Auth",
};

const CATEGORY_ORDER = ["Documents", "Notifications", "Auth"];

export default function FeatureFlags() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isToggling, setIsToggling] = useState<number | null>(null);

  const {
    data: flags,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin-feature-flags"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("feature_flags")
        .select("*")
        .order("display_name", { ascending: true });

      if (error) throw error;
      return (data || []) as FeatureFlag[];
    },
  });

  const categories = useMemo((): FlagCategory[] => {
    if (!flags) return [];

    const grouped: Record<string, FeatureFlag[]> = {};

    for (const flag of flags) {
      const category = FLAG_CATEGORIES[flag.name] || "Other";
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(flag);
    }

    return [...CATEGORY_ORDER, "Other"]
      .filter((cat) => grouped[cat]?.length)
      .map((cat) => ({ label: cat, flags: grouped[cat] }));
  }, [flags]);

  const toggleFlagMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: number; enabled: boolean }) => {
      const { error } = await supabase.from("feature_flags").update({ enabled }).eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-feature-flags"] });
    },
    onError: (err) => {
      console.error("Error toggling feature flag:", err);
      enqueueSnackbar("Could not update feature flag. Please try again.", { variant: "error" });
    },
  });

  const handleToggle = async (id: number, enabled: boolean) => {
    setIsToggling(id);
    try {
      await toggleFlagMutation.mutateAsync({ id, enabled });
      enqueueSnackbar("Feature flag updated.", { variant: "success" });
    } finally {
      setIsToggling(null);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-subtle">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(POST_LOSS_ROUTES.OVERVIEW)}
            className="flex items-center gap-2 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Exit
          </Button>
          <p className="text-xl font-semibold">Feature Flags</p>
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin h-8 w-8" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-subtle">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(POST_LOSS_ROUTES.OVERVIEW)}
            className="flex items-center gap-2 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Exit
          </Button>
          <p className="text-xl font-semibold">Feature Flags</p>
          <div className="text-center py-8">
            <p className="text-destructive mb-4">Failed to load feature flags</p>
            <Button onClick={() => refetch()}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-subtle">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(POST_LOSS_ROUTES.OVERVIEW)}
          className="flex items-center gap-2 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Exit
        </Button>

        <div className="mb-8">
          <p className="text-xl font-semibold">Feature Flags</p>
          <p className="text-sm text-secondary mt-1">
            Manage feature flags to control application functionality
          </p>
        </div>

        {flags && flags.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-secondary">No feature flags found</p>
          </div>
        ) : (
          <div className="space-y-8">
            {categories.map((category) => (
              <div key={category.label} className="space-y-3">
                <p className="text-base font-semibold text-secondary">{category.label}</p>
                {category.flags.map((flag) => (
                  <Card key={flag.id} className="shadow-none">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{flag.display_name}</p>
                          {flag.desc && (
                            <p className="text-2xs text-secondary mt-0.5">{flag.desc}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 ml-4">
                          <Switch
                            checked={flag.enabled}
                            onCheckedChange={() => handleToggle(flag.id, !flag.enabled)}
                            disabled={isToggling === flag.id}
                          />
                          {isToggling === flag.id && <Loader2 className="h-4 w-4 animate-spin" />}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
